import { api } from '../api';
import { getCurrentRole, getTelegramState, requireAuthOrRedirect, setAuthHint } from '../auth';
import { isSubsRole } from '../roles';
import { askPassword, checkEmail, checkNickname, checkPassword } from '../account-forms';
import { toast } from '../utils';
import { setupDropdown } from './settings';

/**
 * "Керування профілем" (/ctrlprof) — лише для гостьового акаунта: нікнейм, безпека (Telegram), видалення акаунта.
 * Усі чутливі дії підтверджуються поточним паролем; сервер перевіряє все сам (routes/auth.ts).
 */
export async function initCtrlprof() {
  const username = await requireAuthOrRedirect();
  if (!username) return;
  if (!isSubsRole(await getCurrentRole())) {
    // Для акаунтів із users.json цього розділу немає (у них немає ні пошти, ні нікнейма).
    location.replace('/more');
    return;
  }

  setupDropdown('nick-toggle', 'nick-body', 'nick-chevron');
  setupDropdown('security-toggle', 'security-body', 'security-chevron');
  setupDropdown('email-toggle', 'email-body', 'email-chevron');
  setupDropdown('pass-toggle', 'pass-body', 'pass-chevron');
  setupDropdown('tg-toggle', 'tg-body', 'tg-chevron');
  setupDropdown('delete-toggle', 'delete-body', 'delete-chevron');

  setupNickname();
  setupPasswordChange();
  setupEmailChange();
  void setupTelegram();
  setupDelete();
}

const byId = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

function showMsg(el: HTMLElement, text: string, ok = false) {
  el.textContent = text;
  el.style.display = text ? 'block' : 'none';
  el.classList.toggle('ctrl-ok', ok);
}

function setupPasswordChange() {
  const cur = byId<HTMLInputElement>('pass-current');
  const next = byId<HTMLInputElement>('pass-new');
  const confirm = byId<HTMLInputElement>('pass-confirm');
  const msg = byId('pass-msg');
  const btn = byId<HTMLButtonElement>('pass-submit');

  byId('pass-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const err = cur.value ? checkPassword(next.value, confirm.value) : 'Введіть поточний пароль.';
    if (err) return showMsg(msg, err);
    if (next.value === cur.value) return showMsg(msg, 'Новий пароль має відрізнятися від поточного.');
    showMsg(msg, '');
    btn.disabled = true;
    try {
      await api.changePassword(cur.value, next.value, confirm.value);
      cur.value = next.value = confirm.value = '';
      showMsg(msg, 'Пароль змінено. На інших пристроях потрібно буде увійти заново.', true);
    } catch (e2) {
      showMsg(msg, (e2 as Error).message || 'Не вдалося змінити пароль.');
    } finally {
      btn.disabled = false;
    }
  });
}

function setupEmailChange() {
  const input = byId<HTMLInputElement>('email-new');
  const pass = byId<HTMLInputElement>('email-password');
  const msg = byId('email-msg');
  const btn = byId<HTMLButtonElement>('email-submit');

  byId('email-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = input.value.trim();
    const err = checkEmail(email) ?? (pass.value ? null : 'Введіть пароль.');
    if (err) return showMsg(msg, err);
    showMsg(msg, '');
    btn.disabled = true;
    try {
      await api.changeEmail(email, pass.value);
      input.value = pass.value = '';
      showMsg(msg, 'Email змінено. Тепер входьте з новим email.', true);
    } catch (e2) {
      showMsg(msg, (e2 as Error).message || 'Не вдалося змінити email.');
    } finally {
      btn.disabled = false;
    }
  });
}

function setupNickname() {
  const input = byId<HTMLInputElement>('nick-new');
  const pass = byId<HTMLInputElement>('nick-password');
  const msg = byId('nick-msg');
  const btn = byId<HTMLButtonElement>('nick-submit');

  byId('nick-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const nickname = input.value.trim();
    const err = checkNickname(nickname) ?? (pass.value ? null : 'Введіть пароль.');
    if (err) return showMsg(msg, err);
    showMsg(msg, '');
    btn.disabled = true;
    try {
      await api.changeNickname(nickname, pass.value);
      input.value = '';
      pass.value = '';
      showMsg(msg, 'Нікнейм змінено.', true);
    } catch (e2) {
      showMsg(msg, (e2 as Error).message || 'Не вдалося змінити нікнейм.');
    } finally {
      btn.disabled = false;
    }
  });
}

/**
 * Прив'язка Telegram для відновлення пароля. Посилання веде до бота (src/lib/telegram-bot.ts); поки відкрита
 * панель прив'язки, раз на 3 с питаємо /api/auth/me, чи вже прив'язано, — і оновлюємо стан без перезавантаження.
 */
async function setupTelegram() {
  const section = byId('telegram-section');
  const unavailableEl = byId('telegram-unavailable');
  const stateEl = byId('telegram-state');
  const linkBtn = byId('telegram-link-btn');
  const linkLabel = byId('telegram-link-label');
  const panel = byId('telegram-link-panel');
  const openLink = byId<HTMLAnchorElement>('telegram-open-link');

  let poll: ReturnType<typeof setInterval> | null = null;
  const stopPoll = () => {
    if (poll) clearInterval(poll);
    poll = null;
  };

  function paint(linked: boolean, available: boolean) {
    stopPoll();
    panel.style.display = 'none';
    const hide = !linked && !available; // бота не налаштовано — прив'язувати нічого
    section.style.display = hide ? 'none' : '';
    unavailableEl.style.display = hide ? '' : 'none';
    stateEl.textContent = linked ? 'Telegram прив’язано' : 'Telegram не прив’язано';
    linkLabel.textContent = linked ? 'Відв’язати Telegram' : 'Прив’язати Telegram';
    linkBtn.dataset.linked = linked ? '1' : '0';
    linkBtn.classList.toggle('tg-btn-secondary', linked);
  }

  const initial = await getTelegramState();
  paint(initial.linked, initial.available);

  linkBtn.addEventListener('click', async () => {
    if (linkBtn.dataset.linked === '1') {
      askPassword({
        title: 'Відв’язати Telegram?',
        message: 'Без прив’язаного Telegram відновити пароль буде неможливо. Для підтвердження введіть поточний пароль.',
        submitText: 'Відв’язати',
        onSubmit: async (password) => {
          await api.telegramUnlink(password);
          toast('Telegram відв’язано');
          paint(false, true);
        },
      });
      return;
    }
    try {
      const res = await api.telegramLink();
      openLink.href = res.url;
      panel.style.display = 'block';
      stopPoll();
      const startedAt = Date.now();
      poll = setInterval(async () => {
        if (Date.now() - startedAt > 10 * 60 * 1000) return stopPoll();
        try {
          const me = await api.me();
          if (me.telegramLinked) {
            toast('Telegram прив’язано');
            paint(true, true);
          }
        } catch {
          /* тимчасова помилка — спробуємо ще раз */
        }
      }, 3000);
    } catch (err) {
      toast((err as Error).message);
    }
  });
}

function setupDelete() {
  const pass = byId<HTMLInputElement>('delete-password');
  const msg = byId('delete-msg');
  const btn = byId<HTMLButtonElement>('delete-submit');

  byId('delete-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!pass.value) return showMsg(msg, 'Введіть пароль.');
    showMsg(msg, '');
    btn.disabled = true;
    try {
      await api.deleteAccount(pass.value);
      setAuthHint(null);
      location.replace('/more');
    } catch (e2) {
      showMsg(msg, (e2 as Error).message || 'Не вдалося видалити акаунт.');
      btn.disabled = false;
    }
  });
}
